import { useEffect } from 'react';

/**
 * Enhanced SEO Component - Updates document head with page-specific meta tags
 * Includes support for Product schema, Breadcrumbs, FAQ, Video, Service, and ItemList schemas
 */
export const SEO = ({ 
  title, 
  description, 
  keywords,
  canonical,
  type = 'website',
  image = '/images/optimized/og-image.webp',
  product = null,
  breadcrumbs = null,
  faq = null,
  videos = null,
  robots = 'index, follow',
  service = null,
  itemList = null,
  article = null
}) => {
  useEffect(() => {
    // Update title
    const fullTitle = title 
      ? `${title} | Pruva Jacuzzi Dubai` 
      : 'Pruva Jacuzzi Dubai | Premium Spa & Jacuzzi Systems UAE';
    document.title = fullTitle;

    // Update robots meta tag
    let metaRobots = document.querySelector('meta[name="robots"]');
    if (!metaRobots) {
      metaRobots = document.createElement('meta');
      metaRobots.setAttribute('name', 'robots');
      document.head.appendChild(metaRobots);
    }
    metaRobots.setAttribute('content', robots);

    // Update meta description
    if (description) {
      let metaDesc = document.querySelector('meta[name="description"]');
      if (metaDesc) metaDesc.setAttribute('content', description);
      
      let ogDesc = document.querySelector('meta[property="og:description"]');
      if (ogDesc) ogDesc.setAttribute('content', description);
      
      let twDesc = document.querySelector('meta[property="twitter:description"]');
      if (twDesc) twDesc.setAttribute('content', description);
    }

    // Update meta keywords
    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) metaKeywords.setAttribute('content', keywords);
    }

    // Update OG title
    let ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) ogTitle.setAttribute('content', fullTitle);
    
    let twTitle = document.querySelector('meta[property="twitter:title"]');
    if (twTitle) twTitle.setAttribute('content', fullTitle);

    // Update canonical URL
    if (canonical) {
      let canonicalLink = document.querySelector('link[rel="canonical"]');
      if (canonicalLink) canonicalLink.setAttribute('href', canonical);
      
      let ogUrl = document.querySelector('meta[property="og:url"]');
      if (ogUrl) ogUrl.setAttribute('content', canonical);
    }

    // Update OG image
    if (image) {
      let ogImage = document.querySelector('meta[property="og:image"]');
      if (ogImage) ogImage.setAttribute('content', image);
      
      let twImage = document.querySelector('meta[property="twitter:image"]');
      if (twImage) twImage.setAttribute('content', image);
    }

    // Update OG type
    let ogType = document.querySelector('meta[property="og:type"]');
    if (ogType) ogType.setAttribute('content', type === 'product' ? 'product' : 'website');

    // Remove existing dynamic schema scripts
    document.querySelectorAll('script[data-seo-schema]').forEach(el => el.remove());

    // Add Product Schema with enhanced properties
    if (product) {
      const productSchema = {
        "@context": "https://schema.org",
        "@type": "Product",
        "name": product.name,
        "description": product.description,
        "image": product.image,
        "sku": product.sku || product.name?.replace(/\s+/g, '-').toUpperCase(),
        "mpn": product.sku || product.name?.replace(/\s+/g, '-').toUpperCase(),
        "category": product.category || "Spa & Jacuzzi Systems",
        "brand": {
          "@type": "Brand",
          "name": "Pruva Jacuzzi"
        },
        "manufacturer": {
          "@type": "Organization",
          "name": "Pruva Acrylic Swimming Pools and Installation Works LLC",
          "url": typeof window !== 'undefined' ? window.location.origin : "https://www.pruvajacuzzi.com"
        },
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "priceCurrency": "AED",
          "priceValidUntil": new Date(new Date().setFullYear(new Date().getFullYear() + 1)).toISOString().split('T')[0],
          "itemCondition": "https://schema.org/NewCondition",
          "seller": {
            "@type": "Organization",
            "name": "Pruva Jacuzzi Dubai",
            "url": typeof window !== 'undefined' ? window.location.origin : "https://www.pruvajacuzzi.com"
          }
        },
        "additionalProperty": product.specs || []
      };
      
      const productScript = document.createElement('script');
      productScript.type = 'application/ld+json';
      productScript.setAttribute('data-seo-schema', 'product');
      productScript.textContent = JSON.stringify(productSchema);
      document.head.appendChild(productScript);
    }

    // Add Breadcrumb Schema
    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": breadcrumbs.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": item.name,
          "item": item.url
        }))
      };
      
      const breadcrumbScript = document.createElement('script');
      breadcrumbScript.type = 'application/ld+json';
      breadcrumbScript.setAttribute('data-seo-schema', 'breadcrumb');
      breadcrumbScript.textContent = JSON.stringify(breadcrumbSchema);
      document.head.appendChild(breadcrumbScript);
    }

    // Add FAQ Schema
    if (faq && faq.length > 0) {
      const faqSchema = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": faq.map(item => ({
          "@type": "Question",
          "name": item.question,
          "acceptedAnswer": {
            "@type": "Answer",
            "text": item.answer
          }
        }))
      };
      
      const faqScript = document.createElement('script');
      faqScript.type = 'application/ld+json';
      faqScript.setAttribute('data-seo-schema', 'faq');
      faqScript.textContent = JSON.stringify(faqSchema);
      document.head.appendChild(faqScript);
    }

    // Add VideoObject Schema for hero videos
    if (videos && videos.length > 0) {
      videos.forEach((video, index) => {
        const videoSchema = {
          "@context": "https://schema.org",
          "@type": "VideoObject",
          "name": video.name || `Pruva Jacuzzi Video ${index + 1}`,
          "description": video.description || "Premium jacuzzi and spa systems by Pruva - engineered for luxury wellness in Dubai and UAE.",
          "thumbnailUrl": video.thumbnailUrl || "/images/optimized/og-image.webp",
          "uploadDate": video.uploadDate || "2024-12-01",
          "contentUrl": video.url,
          "embedUrl": video.url,
          "duration": video.duration || "PT30S",
          "publisher": {
            "@type": "Organization",
            "name": "Pruva Jacuzzi Dubai",
            "logo": {
              "@type": "ImageObject",
              "url": "/images/optimized/og-image.webp"
            }
          }
        };
        
        const videoScript = document.createElement('script');
        videoScript.type = 'application/ld+json';
        videoScript.setAttribute('data-seo-schema', `video-${index}`);
        videoScript.textContent = JSON.stringify(videoSchema);
        document.head.appendChild(videoScript);
      });
    }

    // Add Service Schema for service pages
    if (service) {
      const serviceSchema = {
        "@context": "https://schema.org",
        "@type": "Service",
        "serviceType": service.type || "Spa & Jacuzzi Installation",
        "name": service.name,
        "description": service.description,
        "provider": {
          "@type": "Organization",
          "name": "Pruva Jacuzzi Dubai",
          "url": typeof window !== 'undefined' ? window.location.origin : "https://www.pruvajacuzzi.com",
          "telephone": "+971-54-218-4086",
          "email": "info@pruvajacuzzi.com"
        },
        "areaServed": {
          "@type": "Place",
          "name": "Dubai, UAE"
        },
        "hasOfferCatalog": service.offerings ? {
          "@type": "OfferCatalog",
          "name": service.name,
          "itemListElement": service.offerings.map((offer, i) => ({
            "@type": "Offer",
            "itemOffered": {
              "@type": "Service",
              "name": offer
            }
          }))
        } : undefined
      };
      
      const serviceScript = document.createElement('script');
      serviceScript.type = 'application/ld+json';
      serviceScript.setAttribute('data-seo-schema', 'service');
      serviceScript.textContent = JSON.stringify(serviceSchema);
      document.head.appendChild(serviceScript);
    }

    // Add ItemList Schema for category/collection pages
    if (itemList && itemList.items && itemList.items.length > 0) {
      const itemListSchema = {
        "@context": "https://schema.org",
        "@type": "ItemList",
        "name": itemList.name || "Product Collection",
        "description": itemList.description,
        "numberOfItems": itemList.items.length,
        "itemListElement": itemList.items.map((item, index) => ({
          "@type": "ListItem",
          "position": index + 1,
          "name": item.name,
          "url": item.url,
          "image": item.image
        }))
      };
      
      const itemListScript = document.createElement('script');
      itemListScript.type = 'application/ld+json';
      itemListScript.setAttribute('data-seo-schema', 'itemlist');
      itemListScript.textContent = JSON.stringify(itemListSchema);
      document.head.appendChild(itemListScript);
    }

    // Add BlogPosting Schema
    if (article) {
      const articleSchema = {
        "@context": "https://schema.org",
        "@type": "BlogPosting",
        "headline": article.headline,
        "description": article.description,
        "image": article.image,
        "datePublished": article.datePublished,
        "dateModified": article.datePublished,
        "author": { "@type": "Organization", "name": "Pruva Jacuzzi" },
        "publisher": {
          "@type": "Organization",
          "name": "Pruva Jacuzzi Dubai",
          "url": typeof window !== 'undefined' ? window.location.origin : "https://www.pruvajacuzzi.com"
        },
        "mainEntityOfPage": { "@type": "WebPage", "@id": article.url }
      };

      const articleScript = document.createElement('script');
      articleScript.type = 'application/ld+json';
      articleScript.setAttribute('data-seo-schema', 'article');
      articleScript.textContent = JSON.stringify(articleSchema);
      document.head.appendChild(articleScript);
    }

    // Cleanup on unmount
    return () => {
      document.querySelectorAll('script[data-seo-schema]').forEach(el => el.remove());
    };

  }, [title, description, keywords, canonical, type, image, product, breadcrumbs, faq, videos, robots, service, itemList, article]);

  return null;
};

export default SEO;
